import { apiMethods, apiEndpoints, uploadToCloudflareR2Proxy, downloadFromCloudflareR2 } from './api'
import { UPLOAD_CONFIG } from '../utils/uploadConstants'

class VideoService {
  // Get all videos for current user
  async getVideos() {
    const response = await apiMethods.get(apiEndpoints.videos.list)
    return response.data.videos || []
  }





  // Update video metadata
  async updateVideo(videoId, metadata) {
    const response = await apiMethods.put(apiEndpoints.videos.update(videoId), {
      title: metadata.title,
      description: metadata.description,
      category: metadata.category,
      tags: metadata.tags || [],
      visibility: metadata.visibility
    })
    
    return response.data.video
  }

  // Delete video
  async deleteVideo(videoId) {
    const response = await apiMethods.delete(apiEndpoints.videos.delete(videoId))
    return response.data
  }

  // Get download URL for video playback
  async getDownloadUrl(videoId) {
    const response = await apiMethods.get(apiEndpoints.videos.downloadUrl(videoId))
    return response.data
  }


  // Get video metadata
  async getVideoMetadata(videoId) {
    const response = await apiMethods.get(apiEndpoints.videos.metadata(videoId))
    return response.data.video
  }

  // Video categories
  getVideoCategories() {
    return [
      'Family Events',
      'Holidays',
      'Birthdays',
      'Vacations',
      'Kids',
      'Pets',
      'Home Videos',
      'Special Occasions',
      'Other'
    ]
  }

  // Video visibility options
  getVisibilityOptions() {
    return [
      { value: 'private', label: 'Private (Family Only)' },
      { value: 'restricted', label: 'Restricted (Selected Users)' },
      { value: 'unlisted', label: 'Unlisted (Link Only)' }
    ]
  }

  // File validation
  validateVideoFile(file) {
    // Use enterprise constants for validation
    const maxSize = UPLOAD_CONFIG.MAX_FILE_SIZE_BYTES // 10GB enterprise standard
    const allowedTypes = [
      'video/mp4',
      'video/avi',
      'video/mov',
      'video/wmv',
      'video/flv',
      'video/webm',
      'video/mkv',
      'video/m4v'
    ]

    if (!file) {
      throw new Error('No file selected')
    }

    if (file.size > maxSize) {
      const maxSizeGB = Math.round(maxSize / (1024 * 1024 * 1024))
      throw new Error(`File too large. Maximum size is ${maxSizeGB}GB.`)
    }

    if (!allowedTypes.includes(file.type)) {
      throw new Error('Invalid file type. Please upload a video file.')
    }

    return true
  }

  // Generate video thumbnail URL from R2 storage
  getThumbnailUrl(video, options = {}) {
    // Use R2 thumbnail if available, fallback to placeholder
    if (video.thumbnail_url) {
      return video.thumbnail_url
    }

    // Generate placeholder or extract from video
    return '/placeholder-thumbnail.jpg'
  }

  // Generate video preview URL (not supported with R2 - return null)
  getPreviewUrl(video, options = {}) {
    // R2 doesn't support automatic preview generation
    return null
  }

  // Generate direct URL to video in R2
  getVideoUrl(video) {
    return video.stream_url || video.r2_url || null
  }

  // Format file size
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes'
    
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  // Format duration
  formatDuration(seconds) {
    if (!seconds || seconds < 0) return '0:00'
    
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const remainingSeconds = Math.floor(seconds % 60)
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
    }
    
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  // Search and filter helpers
  searchVideos(videos, query) {
    if (!query || !query.trim()) {
      return videos
    }
    
    const searchTerm = query.toLowerCase().trim()
    
    return videos.filter(video => {
      return (
        video.title.toLowerCase().includes(searchTerm) ||
        video.description.toLowerCase().includes(searchTerm) ||
        video.category.toLowerCase().includes(searchTerm) ||
        video.tags.some(tag => tag.toLowerCase().includes(searchTerm))
      )
    })
  }

  filterVideosByCategory(videos, category) {
    if (!category || category === 'all') {
      return videos
    }
    
    return videos.filter(video => video.category === category)
  }

  sortVideos(videos, sortBy = 'newest') {
    const sorted = [...videos]
    
    switch (sortBy) {
      case 'newest':
        return sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      case 'oldest':
        return sorted.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
      case 'title':
        return sorted.sort((a, b) => a.title.localeCompare(b.title))
      case 'duration':
        return sorted.sort((a, b) => (b.duration || 0) - (a.duration || 0))
      case 'size':
        return sorted.sort((a, b) => (b.fileSize || 0) - (a.fileSize || 0))
      default:
        return sorted
    }
  }
}

export const videoService = new VideoService()
export default videoService